import React, { useState, useEffect } from 'react';
import { MapPin, Save, Locate } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const LocationSettings = ({ userProfile, onSave, saving }) => {
  const [locationData, setLocationData] = useState({
    location_country: '',
    location_city: '',
    notification_radius: 50 // kilometers
  });

  const [errors, setErrors] = useState({});
  const [gettingLocation, setGettingLocation] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setLocationData({
        location_country: userProfile?.location_country || '',
        location_city: userProfile?.location_city || '',
        notification_radius: userProfile?.notification_radius || 50
      });
    }
  }, [userProfile]);

  const handleChange = (field, value) => {
    setLocationData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getCurrentLocation = () => {
    if (!navigator?.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    setGettingLocation(true);
    navigator?.geolocation?.getCurrentPosition(
      async (position) => {
        try {
          // In a real app, you'd use a geocoding service to convert coordinates to city/country // For now, we'll just set mock data
          setLocationData(prev => ({
            ...prev,
            location_city: 'Current City',
            location_country: 'Current Country'
          }));
        } catch (error) {
          console.error('Error getting location details:', error);
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        setGettingLocation(false);
        alert('Unable to retrieve your location. Please enter it manually.');
      }
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!locationData?.location_country?.trim()) {
      newErrors.location_country = 'Country is required';
    }

    if (!locationData?.location_city?.trim()) {
      newErrors.location_city = 'City is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(locationData);
    }
  };

  const countries = [
    { value: '', label: 'Select Country' },
    { value: 'United States', label: 'United States' },
    { value: 'Canada', label: 'Canada' },
    { value: 'United Kingdom', label: 'United Kingdom' },
    { value: 'Australia', label: 'Australia' },
    { value: 'Germany', label: 'Germany' },
    { value: 'France', label: 'France' },
    { value: 'Japan', label: 'Japan' },
    { value: 'Other', label: 'Other' }
  ];

  const radiusOptions = [
    { value: 10, label: '10 km' },
    { value: 25, label: '25 km' },
    { value: 50, label: '50 km' },
    { value: 100, label: '100 km' },
    { value: 200, label: '200 km' }
  ];

  return (
    <div className="p-6">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <MapPin className="h-5 w-5 mr-2" />
          Location Settings
        </h2>
        <p className="text-gray-600 mt-1">
          Set your location preferences for relevant outage notifications
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <MapPin className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-900">Location-Based Notifications</h3>
              <p className="text-sm text-blue-700 mt-1">
                Your location is used to show you relevant outages in your area and send location-based notifications.
                We respect your privacy and only use this information for service improvements.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <Select
              value={locationData?.location_country}
              onValueChange={(value) => handleChange('location_country', value)}
              placeholder="Select your country"
              error={errors?.location_country}
            >
              {countries?.map((country) => (
                <option key={country?.value} value={country?.value}>
                  {country?.label}
                </option>
              ))}
            </Select>
            {errors?.location_country && (
              <p className="text-red-600 text-sm mt-1">{errors?.location_country}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City
            </label>
            <div className="flex space-x-2">
              <div className="flex-1">
                <Input
                  type="text"
                  value={locationData?.location_city}
                  onChange={(e) => handleChange('location_city', e?.target?.value)}
                  placeholder="Enter your city"
                  error={errors?.location_city}
                />
                {errors?.location_city && (
                  <p className="text-red-600 text-sm mt-1">{errors?.location_city}</p>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={getCurrentLocation}
                disabled={gettingLocation}
                className="flex items-center"
              >
                {gettingLocation ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                ) : (
                  <Locate className="h-4 w-4 mr-2" />
                )}
                {gettingLocation ? 'Getting...' : 'Auto-detect'}
              </Button>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notification Radius
          </label>
          <div className="max-w-xs">
            <Select
              value={locationData?.notification_radius?.toString()}
              onValueChange={(value) => handleChange('notification_radius', parseInt(value))}
              placeholder="Select notification radius"
            >
              {radiusOptions?.map((option) => (
                <option key={option?.value} value={option?.value?.toString()}>
                  {option?.label}
                </option>
              ))}
            </Select>
            <p className="text-gray-500 text-sm mt-1">
              Receive notifications for outages within this distance from your location
            </p>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Privacy Notice</h3>
          <p className="text-sm text-gray-600">
            Your location information is stored securely and used only to provide you with relevant outage notifications.
            You can update or remove your location information at any time. We do not share your precise location
            with third parties.
          </p>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <Button
            type="submit"
            disabled={saving}
            className="flex items-center"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Location Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default LocationSettings;